import type { Activity } from './Activity';
import type { Project } from './Project';
import type { User } from './User';
import type { Asset } from './Asset';
import type { ForemanArea } from './ForemanArea';
import { ContractArea } from './ContractArea';
import type { Effort } from './Effort';

/**
 * Derived effort figures the task list needs without the effort rows themselves.
 *
 * Mirrors `TaskEffortSummary` in server/src/modules/tasks/quantifiedTask.list.service.ts - the two
 * must stay in step, since the markup arithmetic on this side assumes `revenueBase` holds
 * pre-markup per-type subtotals.
 */
export interface TaskEffortSummary {
  count: number;
  /** ISO string, or null when no effort carries a usable date. */
  earliestDate: string | null;
  /** ISO string, or null. The completion modal derives a default completion date from this. */
  latestDate: string | null;
  hasPriceable: boolean;
  /** Raw cost: Other rows contribute quantity alone; labour uses DRM OT when persisted; else quantity x rate. */
  costTotal: number;
  revenueBase: {
    labour: number;
    material: number;
    subcontractor: number;
    vehicle: number;
    hiredEquipment: number;
    other: number;
  };
}

export enum TaskStage {
  PROPOSED = 'PROPOSED',
  PLANNED = 'PLANNED',
  ACTIVE = 'ACTIVE',
  ACTIVE_MARKED_AS_COMPLETE = 'ACTIVE_MARKED_AS_COMPLETE',
  INTERNAL_REVIEW = 'INTERNAL_REVIEW',
  CLOSED = 'CLOSED',
  FINALIZED = 'FINALIZED',
  CANCELLED = 'CANCELLED'
}

export type TaskType = 'Overhead' | 'Routine' | 'Quantified' | 'Internal' | 'Custom' | 'H200' | 'Generic';

export interface Task {
  id: number;
  description: string;
  instructions: string;
  name?: string;
  type: TaskType;
  status: string;
  mode: string;
  identifiedDate?: Date;
  startDate?: Date;
  completionDate?: Date;
  estimatedEffortHours?: number;
  plannedCompletionDate?: Date;
  completionAccomplishment: number;
  contractYear: string;
  identifiedAccomplishment: number;
  projectId: number;
  taskNumber?: string;
  activities?: Activity[];
  activityId?: number;
  taskLocations?: TaskLocation[];
  quantifiedTasks?: QuantifiedTask[];
  priority?: string;
  taskCostings?: TaskCosting[];
  labours?: Labour[];
  materials?: Material[];
  vehicles?: Vehicle[];
  project?: Project;
  subcontractors?: Subcontractor[];
  taskHistory?: TaskHistory[];
  foremanArea?: ForemanArea;
  taskComments?: TaskComments[];
  /**
   * Present when the full effort rows have been loaded for this task - the task modal, or a list
   * row the user expanded. The paginated list endpoint no longer sends these, so anything running
   * against a list row must tolerate `undefined` and fall back to `effortSummary`.
   */
  efforts?: Effort[];
  /**
   * Always present on rows from the paginated list endpoint. Carries the derived figures the list
   * needs without the rows themselves. The per-type `revenueBase` values are pre-markup, and are
   * exactly the subtotals the Cost Plus formula would have computed from the rows.
   */
  effortSummary?: TaskEffortSummary;
  comments?: string | null;
  foremanAreaId?: number;
  workIdId?: number | null;
  updatedAt?: Date;
  createdAt?: Date;
  contractAreaId?: number;
  contractArea?: ContractArea;
  createdBy?: User;
  createdById?: number;
  taskAttachments?: TaskAttachment[];
  assignedEmployees?: User[];
}

export interface TaskComments {
    id?: number;
    taskId: number;
    task: Task;
    comments: string | null;
    userId: number;
    user: User;
    createdAt: Date;
  } 

  export interface TaskCosting {
    id?: number;
    taskId: number;
    task: Task;
    baseRate: number;
    rateMultiplier: number;
    rateAdjustment: number;
    totalValue: number;
    actualTotalValue?: number;
    rateAdjustmentComments?: string;
    taskType: 'Unit Rate' | 'Lump Sum';
    costType: 'Fixed Cost' | 'Cost Plus';
    labourMarkup?: number;
    materialMarkup?: number;
    subcontractorMarkup?: number;
    vehicleMarkup?: number;
    hiredEquipmentMarkup?: number;
  } 

  export interface TaskLocation {
    id?: number;
    taskId: number;
    type: TaskLocationType;
    latitude: number;
    longitude: number;
    assetId?: number | null;
    task?: Task;
    asset?: Asset;
  } 

  export enum TaskLocationType {
    START = 'START',
    END = 'END',
  }

  export interface QuantifiedTask {
    id?: number;
    taskId: number;
    task?: Task;
    identifiedQuantity: number;
    unitOfMeasure: string;
    foremanArea?: string;
    serviceArea?: string;
  } 
  
  export interface TaskAttachment {
    id?: number;
    taskId: number;
    task: Task;
    attachmentURL: string;
    attachmentNotes?: string;
    attachmentName: string;
    attachmentType?: string; // Type of attachment (e.g., 'image', 'document', 'pdf')
  }

export interface TaskHistory {
  id: number;
  taskId: number;
  comments?: string | null;
  summary?: string | null;
  stage: string;
  external: boolean;
  userId: number;
  user: User;
  createdAt: Date;
}

export interface Labour {
  id: number;
  hours: number;
  rate: number;
  comments?: string;
  unitOfMeasure: string;
  classType: string;
}

export interface Material {
  id: number;
  quantity: number;
  unitCost: number;
  materialUsed: string;
  unitOfMeasure: string;
}

export interface Vehicle {
  id: number;
  unitHours: number;
  rate: number;
  unitUsed: string;
  unitOfMeasure: string;
}

export interface Subcontractor {
  id: number;
  unitHours: number;
  rate: number;
  equipmentUsed: string;
  unitOfMeasure: string;
  equipmentHours: number;
}
